import React, { useEffect, useState } from 'react';
import { Header } from '../../components/layout/Header';
import { KPICard } from '../../components/dashboard/KPICard';
import { TrendChart, BarChartComponent, PieChartComponent } from '../../components/dashboard/Charts';
import { reportsApi } from '../../lib/api';
import { toast } from 'sonner';
import { formatNumber, calculatePercentage, formatDateRange } from '../../lib/utils';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../components/ui/select';
import { 
    Linkedin, Target, Users, Send, UserCheck, MessageSquare,
    ThumbsUp, ThumbsDown, Clock, TrendingUp, Award, User,
    Calendar, Building, Briefcase
} from 'lucide-react';

export default function LinkedInPage() {
    const [loading, setLoading] = useState(true);
    const [reports, setReports] = useState([]);
    const [selectedReportId, setSelectedReportId] = useState('');
    const [allProfiles, setAllProfiles] = useState([]);
    const [hasData, setHasData] = useState(false);

    useEffect(() => { loadData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const loadData = async () => {
        try {
            const response = await reportsApi.getAll();
            setReports(response.data);
            if (response.data.length > 0) {
                setSelectedReportId(response.data[0].id);
                setHasData(true);
                
                // Aggregate all profile data across reports
                const profileMap = {};
                response.data.forEach(report => {
                    (report.linkedin?.profiles || []).forEach(profile => {
                        const name = profile.profile_name;
                        if (!name) return;
                        if (!profileMap[name]) {
                            profileMap[name] = {
                                profile_name: name,
                                reachouts_sent: 0, connection_accepted: 0, responses: 0,
                                positive_responses: 0, rejections: 0, in_progress: 0,
                                mqls: 0, qualified_leads: 0, report_count: 0,
                                target_industries: profile.target_industries || [],
                                target_company_sizes: profile.target_company_sizes || [],
                                target_designations: profile.target_designations || [],
                            };
                        }
                        profileMap[name].reachouts_sent += profile.reachouts_sent || 0;
                        profileMap[name].connection_accepted += profile.connection_accepted || 0;
                        profileMap[name].responses += profile.responses || 0;
                        profileMap[name].positive_responses += profile.positive_responses || 0;
                        profileMap[name].rejections += profile.rejections || 0;
                        profileMap[name].in_progress += profile.in_progress || 0;
                        profileMap[name].mqls += profile.mqls || 0;
                        profileMap[name].qualified_leads += profile.qualified_leads || 0;
                        profileMap[name].report_count += 1;
                        // Keep latest target audience data
                        if (profile.target_industries?.length) profileMap[name].target_industries = profile.target_industries;
                        if (profile.target_company_sizes?.length) profileMap[name].target_company_sizes = profile.target_company_sizes;
                        if (profile.target_designations?.length) profileMap[name].target_designations = profile.target_designations;
                    });
                });
                setAllProfiles(Object.values(profileMap));
            }
        } catch (error) {
            toast.error('Failed to load LinkedIn data');
        } finally {
            setLoading(false);
        }
    };

    const selectedReport = reports.find(r => r.id === selectedReportId);
    const data = selectedReport?.linkedin || {};

    const trendData = reports.slice(0, 8).reverse().map((report, index) => ({
        name: `W${index + 1}`,
        reachouts: report.linkedin?.reachouts_sent || 0,
        responses: report.linkedin?.responses || 0,
        mqls: report.linkedin?.mqls || 0,
    }));

    const industryData = Object.entries(data?.industry_breakdown || {}).map(([name, value]) => ({ name, value }));
    const geoData = Object.entries(data?.geography_breakdown || {}).map(([name, value]) => ({ name, responses: value }));
    const personaData = Object.entries(data?.persona_breakdown || {}).map(([name, value]) => ({ name, value }));

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="w-12 h-12 border-4 border-channel-linkedin/30 border-t-channel-linkedin rounded-full animate-spin" />
            </div>
        );
    }

    if (!hasData) {
        return (
            <div className="min-h-screen" data-testid="linkedin-page">
                <Header title="LinkedIn Performance" subtitle="Outbound ABM metrics and engagement analytics" />
                <div className="p-8">
                    <div className="card-base p-12 text-center">
                        <Linkedin className="w-16 h-16 text-content-muted mx-auto mb-4" />
                        <h3 className="text-xl font-heading font-bold text-white mb-2">No LinkedIn Data Available</h3>
                        <p className="text-content-secondary max-w-md mx-auto">LinkedIn metrics will appear here once your admin team adds report data.</p>
                    </div>
                </div>
            </div>
        );
    }

    const linkedin = data;
    const acceptanceRate = calculatePercentage(linkedin.connection_accepted, linkedin.reachouts_sent);
    const responseRate = calculatePercentage(linkedin.responses, linkedin.reachouts_sent);
    const positiveRate = calculatePercentage(linkedin.positive_responses, linkedin.responses);
    const mqlRate = calculatePercentage(linkedin.mqls, linkedin.reachouts_sent);

    return (
        <div className="min-h-screen" data-testid="linkedin-page">
            <Header title="LinkedIn Performance" subtitle="Outbound ABM metrics and engagement analytics" />

            <div className="p-8 space-y-8">
                {/* Week Selector */}
                <div className="flex items-center gap-4" data-testid="linkedin-week-selector">
                    <Calendar className="w-5 h-5 text-content-muted" />
                    <Select value={selectedReportId} onValueChange={setSelectedReportId}>
                        <SelectTrigger className="input-base w-80">
                            <SelectValue placeholder="Select period" />
                        </SelectTrigger>
                        <SelectContent className="bg-surface-card border-line-default max-h-60">
                            {reports.map((r) => (
                                <SelectItem key={r.id} value={r.id}>
                                    {formatDateRange(r.period_start, r.period_end)} ({r.report_type})
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                <Tabs defaultValue="overview" className="w-full">
                    <TabsList className="bg-surface-card mb-6">
                        <TabsTrigger value="overview" className="data-[state=active]:bg-channel-linkedin data-[state=active]:text-white">Overview</TabsTrigger>
                        <TabsTrigger value="profiles" className="data-[state=active]:bg-channel-linkedin data-[state=active]:text-white">By Profile ({allProfiles.length})</TabsTrigger>
                        {(industryData.length > 0 || geoData.length > 0 || personaData.length > 0) && (
                            <TabsTrigger value="breakdowns" className="data-[state=active]:bg-channel-linkedin data-[state=active]:text-white">Breakdowns</TabsTrigger>
                        )}
                    </TabsList>

                    {/* Overview Tab */}
                    <TabsContent value="overview" className="space-y-8">
                        <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                            <KPICard title="Target Accounts" value={linkedin.target_accounts || 0} icon={Target} color="linkedin" />
                            <KPICard title="Target Contacts" value={linkedin.target_contacts || 0} icon={Users} color="linkedin" />
                            <KPICard title="Reachouts Sent" value={linkedin.reachouts_sent || 0} icon={Send} />
                            <KPICard title="Accepted" value={linkedin.connection_accepted || 0} icon={UserCheck} />
                            <KPICard title="Responses" value={linkedin.responses || 0} icon={MessageSquare} />
                        </section>
                        <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <KPICard title="Positive Responses" value={linkedin.positive_responses || 0} icon={ThumbsUp} color="inbound" />
                            <KPICard title="Rejections" value={linkedin.rejections || 0} icon={ThumbsDown} />
                            <KPICard title="In Progress" value={linkedin.in_progress || 0} icon={Clock} color="ads" />
                            <KPICard title="MQLs" value={linkedin.mqls || 0} icon={TrendingUp} color="red" />
                        </section>

                        <section className="card-base p-6">
                            <h3 className="text-lg font-heading font-bold text-white mb-6">CONVERSION METRICS</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                                {[
                                    { label: 'Acceptance Rate', value: acceptanceRate, color: '#0077B5' },
                                    { label: 'Response Rate', value: responseRate, color: '#DC2626' },
                                    { label: 'Positive Response Rate', value: positiveRate, color: '#10B981' },
                                    { label: 'MQL Rate', value: mqlRate, color: '#DC2626', scale: 5 },
                                ].map(({ label, value, color, scale }) => (
                                    <div key={label} className="space-y-2">
                                        <div className="flex items-center justify-between">
                                            <span className="text-sm text-content-secondary">{label}</span>
                                            <span className="text-lg font-bold" style={{ color }}>{value}%</span>
                                        </div>
                                        <div className="progress-bar">
                                            <div className="progress-bar-fill" style={{ width: `${Math.min(value * (scale || 1), 100)}%`, background: color }} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>

                        {trendData.some(d => d.reachouts > 0 || d.mqls > 0) && (
                            <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                <TrendChart data={trendData} dataKey="reachouts" title="REACHOUT TREND" color="#0077B5" />
                                <TrendChart data={trendData} dataKey="mqls" title="MQL TREND" color="#FF0000" />
                            </section>
                        )}
                    </TabsContent>

                    {/* Profiles Tab */}
                    <TabsContent value="profiles" className="space-y-6">
                        {allProfiles.length === 0 ? (
                            <div className="card-base p-12 text-center">
                                <User className="w-16 h-16 text-content-muted mx-auto mb-4" />
                                <h3 className="text-lg font-heading font-bold text-white mb-2">No Profile Data Yet</h3>
                                <p className="text-content-secondary">Profile-wise data will appear here once added from Admin Reports</p>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {allProfiles.map((profile, index) => {
                                    const pAcceptRate = calculatePercentage(profile.connection_accepted, profile.reachouts_sent);
                                    const pResponseRate = calculatePercentage(profile.responses, profile.reachouts_sent);
                                    const pMqlRate = calculatePercentage(profile.mqls, profile.reachouts_sent);
                                    
                                    return (
                                        <div key={profile.profile_name || `profile-${index}`} className="card-base p-6 hover:border-channel-linkedin/50 transition-colors" data-testid={`linkedin-profile-${index}`}>
                                            <div className="flex items-center gap-3 mb-4">
                                                <div className="w-12 h-12 rounded-full bg-channel-linkedin/20 flex items-center justify-center">
                                                    <Linkedin className="w-6 h-6 text-channel-linkedin" />
                                                </div>
                                                <div>
                                                    <h4 className="font-heading font-bold text-white text-lg">{profile.profile_name}</h4>
                                                    <Badge className="badge badge-info text-xs">{profile.report_count} reports</Badge>
                                                </div>
                                            </div>

                                            {/* Target Audience Section */}
                                            {(profile.target_industries?.length > 0 || profile.target_company_sizes?.length > 0 || profile.target_designations?.length > 0) && (
                                                <div className="mb-4 p-3 rounded-lg bg-surface-app border border-line-default" data-testid={`profile-target-audience-${index}`}>
                                                    <h5 className="text-xs font-bold text-content-muted uppercase tracking-wider mb-2">Target Audience</h5>
                                                    {profile.target_industries?.length > 0 && (
                                                        <div className="flex items-start gap-2 mb-1.5">
                                                            <Building className="w-3.5 h-3.5 text-channel-linkedin mt-0.5 shrink-0" />
                                                            <div className="flex flex-wrap gap-1">
                                                                {profile.target_industries.map((ind) => (
                                                                    <Badge key={ind} className="text-[10px] bg-channel-linkedin/10 text-channel-linkedin border-0">{ind}</Badge>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                    {profile.target_company_sizes?.length > 0 && (
                                                        <div className="flex items-start gap-2 mb-1.5">
                                                            <Users className="w-3.5 h-3.5 text-channel-ads mt-0.5 shrink-0" />
                                                            <div className="flex flex-wrap gap-1">
                                                                {profile.target_company_sizes.map((sz) => (
                                                                    <Badge key={sz} className="text-[10px] bg-channel-ads/10 text-channel-ads border-0">{sz}</Badge>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                    {profile.target_designations?.length > 0 && (
                                                        <div className="flex items-start gap-2">
                                                            <Briefcase className="w-3.5 h-3.5 text-brand-red mt-0.5 shrink-0" />
                                                            <div className="flex flex-wrap gap-1">
                                                                {profile.target_designations.map((des) => (
                                                                    <Badge key={des} className="text-[10px] bg-brand-red/10 text-brand-red border-0">{des}</Badge>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                            
                                            <div className="grid grid-cols-2 gap-4 mb-4">
                                                <div className="bg-surface-app p-3 rounded-lg">
                                                    <p className="text-xs text-content-muted">Reachouts</p>
                                                    <p className="text-xl font-bold text-white">{formatNumber(profile.reachouts_sent)}</p>
                                                </div>
                                                <div className="bg-surface-app p-3 rounded-lg">
                                                    <p className="text-xs text-content-muted">Accepted</p>
                                                    <p className="text-xl font-bold text-channel-linkedin">{formatNumber(profile.connection_accepted)}</p>
                                                </div>
                                                <div className="bg-surface-app p-3 rounded-lg">
                                                    <p className="text-xs text-content-muted">Responses</p>
                                                    <p className="text-xl font-bold text-white">{formatNumber(profile.responses)}</p>
                                                </div>
                                                <div className="bg-surface-app p-3 rounded-lg">
                                                    <p className="text-xs text-content-muted">Positive</p>
                                                    <p className="text-xl font-bold text-status-success">{formatNumber(profile.positive_responses)}</p>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-2 gap-4 mb-4">
                                                <div className="bg-brand-red/10 p-3 rounded-lg border border-brand-red/30">
                                                    <p className="text-xs text-brand-red font-bold">MQLs</p>
                                                    <p className="text-2xl font-bold text-brand-red">{profile.mqls}</p>
                                                </div>
                                                <div className="bg-status-success/10 p-3 rounded-lg border border-status-success/30">
                                                    <p className="text-xs text-status-success font-bold">SQLs</p>
                                                    <p className="text-2xl font-bold text-status-success">{profile.qualified_leads}</p>
                                                </div>
                                            </div>

                                            <div className="space-y-2 pt-3 border-t border-line-default">
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-content-muted">Accept Rate</span>
                                                    <span className="text-channel-linkedin font-bold">{pAcceptRate}%</span>
                                                </div>
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-content-muted">Response Rate</span>
                                                    <span className="text-white font-bold">{pResponseRate}%</span>
                                                </div>
                                                <div className="flex justify-between text-sm">
                                                    <span className="text-content-muted">MQL Rate</span>
                                                    <span className="text-brand-red font-bold">{pMqlRate}%</span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </TabsContent>

                    {/* Breakdowns Tab */}
                    {(industryData.length > 0 || geoData.length > 0 || personaData.length > 0) && (
                        <TabsContent value="breakdowns" className="space-y-6">
                            <section className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                {industryData.length > 0 && <PieChartComponent data={industryData} title="INDUSTRY BREAKDOWN" />}
                                {geoData.length > 0 && <BarChartComponent data={geoData} dataKeys={['responses']} title="GEOGRAPHY BREAKDOWN" />}
                                {personaData.length > 0 && <PieChartComponent data={personaData} title="PERSONA ENGAGEMENT" />}
                            </section>
                        </TabsContent>
                    )}
                </Tabs>
            </div>
        </div>
    );
}
